const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ANIMATION_READY_CLASSNAME = 'scroll-animations-ready';
const SCROLL_ANIMATION_VISIBLE_CLASSNAME = 'scroll-trigger--visible';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
let scrollAnimationObserver;
let observedScrollAnimationElements = new WeakSet();
let scrollAnimationFallbackFrame;
let scrollAnimationFallbackIsInitialized = false;

function revealScrollAnimationTriggerElements(elements, shouldCancelAnimation = false) {
  elements.forEach((element) => {
    element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    element.classList.add(SCROLL_ANIMATION_VISIBLE_CLASSNAME);
    if (shouldCancelAnimation) element.classList.add(SCROLL_ANIMATION_CANCEL_CLASSNAME);
  });
}

function elementIsInViewport(element) {
  const elementBounds = element.getBoundingClientRect();
  return elementBounds.bottom > 0 && elementBounds.top < window.innerHeight;
}

function elementCanAnimate(element) {
  return (
    !element.classList.contains(SCROLL_ANIMATION_VISIBLE_CLASSNAME) &&
    !element.classList.contains('scroll-trigger--design-mode')
  );
}

function revealScrollAnimationTriggerElement(element, index = 0) {
  element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
  if (element.hasAttribute('data-cascade') && !element.style.getPropertyValue('--animation-order')) {
    element.style.setProperty('--animation-order', index);
  }
  element.classList.add(SCROLL_ANIMATION_VISIBLE_CLASSNAME);
}

function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (!element.isIntersecting) return;

    revealScrollAnimationTriggerElement(element.target, index);
    observer.unobserve(element.target);
  });
}

function setupScrollAnimationObserver() {
  if (scrollAnimationObserver) return scrollAnimationObserver;

  scrollAnimationObserver = new IntersectionObserver(onIntersection, {
    rootMargin: '200px 0px 200px 0px',
    threshold: 0.01,
  });

  return scrollAnimationObserver;
}

function revealVisibleScrollAnimationTriggerElements(rootEl = document) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));

  animationTriggerElements.forEach((element, index) => {
    if (!elementCanAnimate(element) || !elementIsInViewport(element)) return;

    revealScrollAnimationTriggerElement(element, index);
    scrollAnimationObserver?.unobserve(element);
  });
}

function scheduleScrollAnimationFallback(rootEl = document) {
  if (scrollAnimationFallbackFrame) return;

  scrollAnimationFallbackFrame = window.requestAnimationFrame(() => {
    scrollAnimationFallbackFrame = null;
    revealVisibleScrollAnimationTriggerElements(rootEl);
  });
}

function initializeScrollAnimationFallback() {
  if (scrollAnimationFallbackIsInitialized) return;

  scrollAnimationFallbackIsInitialized = true;
  window.addEventListener('scroll', () => scheduleScrollAnimationFallback(), { passive: true });
  window.addEventListener('resize', () => scheduleScrollAnimationFallback(), { passive: true });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    revealScrollAnimationTriggerElements(animationTriggerElements, true);
    return;
  }

  const observer = setupScrollAnimationObserver();
  const elementsToObserve = [];

  animationTriggerElements.forEach((element, index) => {
    if (!elementCanAnimate(element) || observedScrollAnimationElements.has(element)) return;

    if (elementIsInViewport(element)) {
      revealScrollAnimationTriggerElement(element, index);
      return;
    }

    element.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    observedScrollAnimationElements.add(element);
    elementsToObserve.push(element);
  });

  document.documentElement.classList.add(SCROLL_ANIMATION_READY_CLASSNAME);
  initializeScrollAnimationFallback();
  elementsToObserve.forEach((element) => observer.observe(element));
  window.setTimeout(() => scheduleScrollAnimationFallback(rootEl), 100);
  window.setTimeout(() => scheduleScrollAnimationFallback(rootEl), 500);
  window.setTimeout(() => scheduleScrollAnimationFallback(rootEl), 1500);
}

function initializeScrollAnimationMutationObserver() {
  if (!('MutationObserver' in window)) return;

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        if (node.classList?.contains(SCROLL_ANIMATION_TRIGGER_CLASSNAME)) {
          initializeScrollAnimationTrigger(node.parentElement || document);
          return;
        }

        if (node.getElementsByClassName?.(SCROLL_ANIMATION_TRIGGER_CLASSNAME).length) {
          initializeScrollAnimationTrigger(node);
        }
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollAnimationMutationObserver();
  initializeScrollZoomAnimationTrigger();
});

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
